// Drawer display math for a cart line's unit price INCLUDING captured
// option price deltas (kickoffs/option-price-math.md).
//
// ⚠️ THE INVARIANT — no double-count with the rep import. The
// submitted line-item `price` field (what actually reaches the
// request row) stays BASE variant price — untouched by this module.
// Only the customer-facing DISPLAY changes: card "each", line totals,
// the drawer subtotal, and the "N items · Est. $X" quick-review line.
// src/lib/request-option-import.ts's applyRequestLineOptions
// reconstructs the full total on the rep's side from parent (base) +
// one custom line item per priced option — so if this module's math
// ever fed back into the submitted price, the rep import would double
// -count every priced option. It never does; see
// item-price.test.ts's round-trip invariant test, which asserts this
// module's total for a fixture item equals splitRequestProperties'
// parent+option-lines reconstruction of the SAME item.
//
// Uses apo-adapter.ts's shared parsePriceSuffix — the ONE parser for
// the ` (+$<amount>)` suffix format, so this can never silently drift
// from what request-option-import.ts (the rep import) counts as
// "priced."
//
// Kept in its own DOM-free module (like render-item-options.ts) so
// it's unit-testable in the node vitest environment — index.ts calls
// boot() at module load and can't be imported there.

import { parsePriceSuffix } from "./apo-adapter";

export interface LineItemPropertyLike {
  name: string;
  value: string;
}

/** Sum of parsed price-suffix deltas across a line's captured
 *  properties. Absent/empty properties, and entries with no suffix, a
 *  non-trailing suffix, or a malformed/non-positive amount, each
 *  contribute 0 — same tolerant behavior as the rep-import parser
 *  (literally the same function). No priced suffixes anywhere → 0,
 *  so `itemUnitPriceWithOptions` below reduces to exactly `basePrice`,
 *  byte-identical to the pre-existing math. */
export function optionsPriceDelta(properties: LineItemPropertyLike[] | null | undefined): number {
  if (!Array.isArray(properties) || properties.length === 0) return 0;
  let total = 0;
  for (const p of properties) {
    const parsed = parsePriceSuffix(String(p?.value ?? ""));
    if (parsed) total += parsed.price;
  }
  return total;
}

/** Unit display price for a cart line: base price + Σ(priced option
 *  deltas). */
export function itemUnitPriceWithOptions(basePrice: number, properties: LineItemPropertyLike[] | null | undefined): number {
  return basePrice + optionsPriceDelta(properties);
}

/** Cart-wide subtotal/estimate: Σ(qty × itemUnitPriceWithOptions).
 *  Drives the drawer subtotal card, the builder layout's inline
 *  summary, and the quick-review "N items · Est. $X" line — one
 *  source of truth so all three read the same number. */
export function cartTotalWithOptions(
  items: Array<{ price: string; qty: number; properties?: LineItemPropertyLike[] | null }>,
): number {
  return items.reduce(
    (s, it) => s + itemUnitPriceWithOptions(parseFloat(it.price) || 0, it.properties) * it.qty,
    0,
  );
}
